import { useTheme } from "next-themes";
import { Toaster as Sonner, toast } from "sonner";
import { useFormFactor } from "@/hooks/use-mobile";

type ToasterProps = React.ComponentProps<typeof Sonner>;

const Toaster = ({ ...props }: ToasterProps) => {
  const { theme = "system" } = useTheme();
  const formFactor = useFormFactor();

  // PTP-012 — on a PHONE this toaster sat on top of the work controls.
  // Measured on WebKit at 390x844: after Resume, the "Ticket timer resumed"
  // toast covered the Stop Work button's centre — `elementFromPoint` returned
  // the toast's <li>, not the button — so a technician tapping Stop hit a
  // notification instead. A phone has no room for a bottom-anchored overlay
  // above a card's own footer controls.
  //
  // Top is not an invention here: the app's OTHER toaster (Radix
  // `ToastViewport`) is already `top-0` below `sm:` and only moves to the
  // bottom-right from `sm:` up. This makes Sonner agree with that convention
  // instead of contradicting it. Tablet and desktop are unchanged.
  const position: ToasterProps["position"] =
    formFactor === "phone" ? "top-center" : "bottom-right";
  // oct20e OPS (F1, PR #13 row 13; R4 :313–317) — same rule as the Radix viewport (ui/toast.tsx): on a phone the toasts
  // start below the sticky top bar (h-16 + 1 px border) instead of covering the app navigation; elsewhere they sit
  // above the floating controls' reserve instead of covering Help / Report an Issue. Sides not named keep Sonner's
  // defaults. `mobileOffset` is what Sonner applies at ≤ 600 px.
  const belowTopBar = { top: "calc(4rem + 1px + 16px)" };
  const offset: ToasterProps["offset"] =
    formFactor === "phone" ? belowTopBar : { bottom: "calc(var(--floating-controls-reserve) + 16px)" };

  return (
    <Sonner
      theme={theme as ToasterProps["theme"]}
      position={position}
      offset={offset}
      mobileOffset={belowTopBar}
      className="toaster group"
      toastOptions={{
        classNames: {
          toast:
            "group toast group-[.toaster]:bg-background group-[.toaster]:text-foreground group-[.toaster]:border-border group-[.toaster]:shadow-lg",
          description: "group-[.toast]:text-muted-foreground",
          actionButton: "group-[.toast]:bg-primary group-[.toast]:text-primary-foreground",
          cancelButton: "group-[.toast]:bg-muted group-[.toast]:text-muted-foreground",
        },
      }}
      {...props}
    />
  );
};

export { Toaster, toast };
